import { ref, type Ref } from 'vue';
import { useElementMeasurements } from './useElementMeasurements';
import type { WillChangeManager } from './useWillChange';

// Типы для зависимостей
export interface AdvMenuItem {
  id: string | number;
  label?: string;
  icon?: string | null;
  activeIcon?: string | null;
  bgColor?: string | null;
  textColor?: string | null;
  indicatorColor?: string | null;
  mode?: 'outline' | 'filled' | 'flat';
  textBlink?: boolean;
  animation?: 'gradient' | 'pulse' | 'shimmer' | 'breathe' | 'glow';
  hidden?: boolean;
}

export interface RAFScheduler {
  schedule: (id: string, callback: () => void, priority?: 'high' | 'normal' | 'low') => void;
  cancel: (id: string) => void;
  cancelAll: () => void;
}

export interface ScrollMeasurements {
  scrollLeft: number;
  scrollWidth: number;
  clientWidth: number;
  version: number;
}

export interface UseSliderScrollOptions {
  gap: Ref<number>;
  measurementCache: ReturnType<typeof useElementMeasurements> | null;
  rafScheduler: RAFScheduler | null;
  willChangeManager: WillChangeManager | null;
  optimizationsEnabled: Ref<{
    rafBatching: boolean;
    willChange: boolean;
  }>;
  isEffectivelyHidden: Ref<boolean>;
}

export function useSliderScroll(options: UseSliderScrollOptions) {
  const {
    gap,
    measurementCache,
    rafScheduler,
    willChangeManager,
    optimizationsEnabled,
    isEffectivelyHidden
  } = options;

  // State
  const canScrollLeft = ref(false);
  const canScrollRight = ref(false);
  const isScrollLocked = ref(false);
  const isScrollAnimating = ref(false);
  const scrollMeasurements = ref<ScrollMeasurements | null>(null);

  let scrollLockTimeout: ReturnType<typeof setTimeout> | null = null;
  let scrollAnimationTimeout: ReturnType<typeof setTimeout> | null = null;

  // Update slider arrows visibility
  const updateSliderArrows = (sliderRef: Ref<HTMLElement | null>) => {
    if (process.env.SERVER || isEffectivelyHidden.value) {
      return;
    }

    if (!sliderRef.value) return;

    try {
      const ARROW_THRESHOLD = 15;

      const cached = scrollMeasurements.value;
      const sliderMeasurement = measurementCache?.cache.value.slider;

      if (cached && sliderMeasurement) {
        canScrollLeft.value = cached.scrollLeft > ARROW_THRESHOLD;
        canScrollRight.value = cached.scrollLeft + cached.clientWidth < cached.scrollWidth - ARROW_THRESHOLD;
      } else {
        if (optimizationsEnabled.value.rafBatching && rafScheduler) {
          rafScheduler.schedule('measure-scroll-properties', () => {
            if (!sliderRef.value) return;

            try {
              const scrollLeft = sliderRef.value.scrollLeft;
              const scrollWidth = sliderRef.value.scrollWidth;
              const clientWidth = sliderRef.value.clientWidth;

              scrollMeasurements.value = {
                scrollLeft,
                scrollWidth,
                clientWidth,
                version: measurementCache?.cache.value.version ?? 0
              };

              canScrollLeft.value = scrollLeft > ARROW_THRESHOLD;
              canScrollRight.value = scrollLeft + clientWidth < scrollWidth - ARROW_THRESHOLD;
            } catch (error) {
              if (!process.env.SERVER) {
                console.error('[MegaMenu] Error measuring scroll:', error);
              }
              canScrollLeft.value = true;
              canScrollRight.value = true;
            }
          }, 'high');
        }
      }
    } catch (error) {
      if (!process.env.SERVER) {
        console.error('[MegaMenu] Error in updateSliderArrows:', error);
      }
      canScrollLeft.value = false;
      canScrollRight.value = false;
    }
  };

  // Scroll slider left or right
  const scrollSlider = (
    direction: 'left' | 'right',
    sliderRef: Ref<HTMLElement | null>
  ) => {
    if (!sliderRef.value) return;

    isScrollAnimating.value = true;
    if (scrollAnimationTimeout) clearTimeout(scrollAnimationTimeout);

    if (sliderRef.value && optimizationsEnabled.value.willChange) {
      willChangeManager?.set(sliderRef.value, ['scroll-position'], 500);
    }

    const scrollAmount = sliderRef.value.clientWidth * 0.6;
    const newScrollLeft =
      direction === 'left'
        ? sliderRef.value.scrollLeft - scrollAmount
        : sliderRef.value.scrollLeft + scrollAmount;

    sliderRef.value.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth',
    });

    scrollAnimationTimeout = setTimeout(() => {
      isScrollAnimating.value = false;
    }, 500);
  };

  // Scroll to active item
  const scrollToActiveItem = async (
    sliderRef: Ref<HTMLElement | null>,
    menuRef: Ref<HTMLElement | null>,
    itemRefs: Ref<Record<string, HTMLElement>>,
    internalActiveId: Ref<string | number | null>,
    items: AdvMenuItem[]
  ) => {
    if (process.env.SERVER || isEffectivelyHidden.value) {
      return;
    }

    try {
      if (!sliderRef.value || !internalActiveId.value) return;

      if (isScrollAnimating.value) {
        return;
      }

      isScrollAnimating.value = true;
      if (scrollAnimationTimeout) clearTimeout(scrollAnimationTimeout);

      const sliderMeasurement = measurementCache?.cache.value.slider;
      const activeItemMeasurement = measurementCache?.getItemMeasurement(internalActiveId.value);

      if (!sliderMeasurement || !activeItemMeasurement) {
        if (optimizationsEnabled.value.rafBatching && rafScheduler) {
          rafScheduler.schedule('measure-all', () => {
            try {
              measurementCache?.measureAll(
                menuRef.value,
                itemRefs.value,
                null,
                sliderRef.value
              );
              // Will be called again after measurements
            } catch (error) {
              if (!process.env.SERVER) {
                console.error('[MegaMenu] Error in measure-all:', error);
              }
              isScrollAnimating.value = false;
            }
          }, 'high');
        }
        isScrollAnimating.value = false;
        return;
      }

      const activeIndex = items.findIndex((item) => item.id === internalActiveId.value);
      const prevItem = items[activeIndex - 1];
      const nextItem = items[activeIndex + 1];

      const prevItemMeasurement = prevItem ? measurementCache?.getItemMeasurement(prevItem.id) : null;
      const nextItemMeasurement = nextItem ? measurementCache?.getItemMeasurement(nextItem.id) : null;

      const currentScroll = sliderRef.value.scrollLeft;

      const leftEdge = prevItemMeasurement
        ? prevItemMeasurement.offsetLeft
        : activeItemMeasurement.offsetLeft;

      const rightEdge = nextItemMeasurement
        ? nextItemMeasurement.offsetLeft + nextItemMeasurement.offsetWidth
        : activeItemMeasurement.offsetLeft + activeItemMeasurement.offsetWidth;

      const sliderWidth = sliderMeasurement.width;
      const visibleLeft = currentScroll;
      const visibleRight = currentScroll + sliderWidth;

      const isHiddenLeft = leftEdge < visibleLeft;
      const isHiddenRight = rightEdge > visibleRight;

      if (isHiddenLeft) {
        const scrollTo = leftEdge - gap.value;

        if (sliderRef.value && optimizationsEnabled.value.willChange) {
          willChangeManager?.set(sliderRef.value, ['scroll-position'], 500);
        }

        sliderRef.value.scrollTo({
          left: Math.max(0, scrollTo),
          behavior: 'smooth' as ScrollBehavior,
        });
      } else if (isHiddenRight) {
        const scrollTo = rightEdge - sliderWidth + gap.value;

        if (sliderRef.value && optimizationsEnabled.value.willChange) {
          willChangeManager?.set(sliderRef.value, ['scroll-position'], 500);
        }

        sliderRef.value.scrollTo({
          left: scrollTo,
          behavior: 'smooth' as ScrollBehavior,
        });
      } else {
        isScrollAnimating.value = false;
        return;
      }

      scrollAnimationTimeout = setTimeout(() => {
        isScrollAnimating.value = false;
      }, 500);
    } catch (error) {
      if (!process.env.SERVER) {
        console.error('[MegaMenu] Error in scrollToActiveItem:', error);
      }
      isScrollAnimating.value = false;
      if (scrollAnimationTimeout) clearTimeout(scrollAnimationTimeout);
    }
  };

  // Lock scroll temporarily
  const lockScroll = () => {
    if (scrollLockTimeout) clearTimeout(scrollLockTimeout);

    isScrollLocked.value = true;
    scrollLockTimeout = setTimeout(() => {
      isScrollLocked.value = false;
    }, 500);
  };

  // Invalidate scroll measurements
  const invalidateScrollMeasurements = () => {
    scrollMeasurements.value = null;
  };

  // Cleanup
  const cleanup = () => {
    if (scrollLockTimeout) {
      clearTimeout(scrollLockTimeout);
      scrollLockTimeout = null;
    }

    if (scrollAnimationTimeout) {
      clearTimeout(scrollAnimationTimeout);
      scrollAnimationTimeout = null;
    }

    scrollMeasurements.value = null;
  };

  return {
    // State
    canScrollLeft,
    canScrollRight,
    isScrollLocked,
    isScrollAnimating,
    scrollMeasurements,

    // Methods
    updateSliderArrows,
    scrollSlider,
    scrollToActiveItem,
    lockScroll,
    invalidateScrollMeasurements,
    cleanup
  };
}
